import { storageKey, writeLocalJson, readLocalJson } from '@authe/fe-commons/storage';
import { stableHash } from '@authe/fe-commons/keys';
import { createLogger, isDebugEnabled } from '@authe/fe-commons/logging';
import { randomUuid, randomHex } from '@authe/fe-commons/ids';
import { computeBackoff as computeBackoff$1 } from '@authe/fe-commons/async';
import { convertRegionToBackupRegion, isRegionEnabled, convertRegionToAlias } from '@authe/fe-commons/region';
import { EventDeduper, BroadcastBus } from '@authe/fe-commons/events';
import * as React5 from 'react';
import { jsx } from 'react/jsx-runtime';
import { toCanonicalChainId } from '@authe/fe-commons/chains';

// src/config.ts
var STREAMING_PACKAGE = "streaming";
var STREAM_BROADCAST_CHANNEL = "authe.stream";
var STREAM_ELECTION_CHANNEL = `${STREAM_BROADCAST_CHANNEL}.election`;
var STREAM_EVENTS_CHANNEL = `${STREAM_BROADCAST_CHANNEL}.events`;
var STREAM_REGION_KEY = storageKey(STREAMING_PACKAGE, "region");
var STREAM_BACKOFF_BASE_MS = 1e3;
var STREAM_BACKOFF_MAX_MS = 3e4;
var STREAM_BACKOFF_FACTOR = 2;
var STREAM_BACKOFF_JITTER = 0.3;
var STREAM_HEARTBEAT_INTERVAL_MS = 15e3;
var STREAM_HEARTBEAT_TIMEOUT_MS = 45e3;
var STREAM_PING_INTERVAL_MS = 2e4;
var STREAM_PING_TIMEOUT_MS = 1e4;
var STREAM_CONNECT_TIMEOUT_MS = 3e4;
var STREAM_HANDSHAKE_TIMEOUT_MS = 1e4;
var STREAM_HTTP_TIMEOUT_MS = 15e3;
var STREAM_REGION_FAILOVER_THRESHOLD = 3;
var STREAM_DEDUPE_CAPACITY = 5e3;
var STREAM_STATE_COALESCE_MS = 250;
var STREAM_LEADER_HEARTBEAT_MS = 2e3;
var STREAM_LEADER_STALE_MS = 6e3;

// src/errors.ts
var ChannelScopedError = class extends Error {
  /** Discriminant so {@link isChannelScopedError} works across bundle realms. */
  channelScoped = true;
  /** The original AppSync error payload, preserved for {@link describeError}. */
  payload;
  constructor(payload) {
    super(describeError(payload));
    this.name = "ChannelScopedError";
    this.payload = payload;
  }
};
function isChannelScopedError(error) {
  return Boolean(
    error && typeof error === "object" && error.channelScoped === true
  );
}
function describeError(error) {
  if (error == null) return String(error);
  if (typeof error === "string") return error;
  if (typeof error === "number" || typeof error === "boolean") return String(error);
  if (error instanceof Error) return error.message || error.name || "Error";
  if (typeof Event !== "undefined" && error instanceof Event) {
    const e = error;
    const bits = [e.type || "event"];
    if (e.code != null) bits.push(`code=${e.code}`);
    if (e.reason) bits.push(`reason=${e.reason}`);
    if (e.message) bits.push(e.message);
    return bits.join(" ");
  }
  if (typeof error === "object") {
    const obj = error;
    if (Array.isArray(obj.errors) && obj.errors.length > 0) {
      return obj.errors.map((e) => describeError(e)).join("; ");
    }
    if (typeof obj.message === "string") {
      return typeof obj.errorType === "string" ? `${obj.errorType}: ${obj.message}` : obj.message;
    }
    if (typeof obj.errorType === "string") return obj.errorType;
    try {
      return JSON.stringify(error);
    } catch {
      return Object.prototype.toString.call(error);
    }
  }
  return String(error);
}

// src/model/status.ts
var ConnectionStatus = {
  /** Not started. */
  IDLE: "idle",
  /** Opening the transport. */
  CONNECTING: "connecting",
  /** Live + receiving. */
  OPEN: "open",
  /** Dropped; backing off before the next attempt. */
  RECONNECTING: "reconnecting",
  /** Open with failed subscriptions, or a stale healthcheck; recovering. */
  DEGRADED: "degraded",
  /** Intentionally stopped. */
  CLOSED: "closed",
  /** Unrecoverable error surfaced to the app. */
  ERROR: "error"
};
function initialConnectionState(region) {
  return {
    status: ConnectionStatus.IDLE,
    region,
    isLeader: false,
    retries: 0,
    lastEventAt: null
  };
}
function connectionStateForChannels(state, ids) {
  if (state.status !== ConnectionStatus.OPEN && state.status !== ConnectionStatus.DEGRADED) return state;
  const channels = ids ? ids.map((id) => state.channels?.[id]) : Object.values(state.channels ?? {});
  const failed = channels.find((channel) => channel?.status === "error");
  if (failed) return { ...state, status: ConnectionStatus.DEGRADED, lastError: failed.error };
  if (channels.some((channel) => !channel || channel.status === "subscribing")) return { ...state, status: ConnectionStatus.CONNECTING };
  return { ...state, status: ConnectionStatus.OPEN, lastError: void 0 };
}

// src/streams/names.ts
var STREAM_STREAMED_TRANSACTIONS = "streamed-transactions";
var STREAM_ADDRESS_WATCH = "address-watch";
var STREAM_MESSAGES = "messages";
var STREAM_TAGS = "tags";
var STREAM_LABELS = "labels";
var STREAM_NOTIFICATIONS = "notifications";
var STREAM_ENTITIES = "entities";
var STREAM_GROUPS = "groups";
var STREAM_TRANSACTION_GROUPS = "transaction-groups";
var STREAM_COLLABORATION = "collaboration";
var STREAM_CHAIN_TIPS = "chain-tips";
var KNOWN_STREAMS = [
  STREAM_STREAMED_TRANSACTIONS,
  STREAM_ADDRESS_WATCH,
  STREAM_MESSAGES,
  STREAM_TAGS,
  STREAM_LABELS,
  STREAM_NOTIFICATIONS,
  STREAM_ENTITIES,
  STREAM_GROUPS,
  STREAM_TRANSACTION_GROUPS,
  STREAM_COLLABORATION,
  STREAM_CHAIN_TIPS
];
var FULL_AUDIENCE = { global: true, tenancy: true, user: true, teams: true };
var STREAM_AUDIENCES = {
  // One channel per row from the dispatcher's `channel_for_row`: tenancy plus
  // the row's `user_id`. Transaction rows carry no teams.
  [STREAM_STREAMED_TRANSACTIONS]: { global: true, tenancy: true, user: true, teams: false },
  // Rung only (address-mgmt-api): the model declares no user/share carrier, and
  // an unscoped hit is dropped rather than broadcast platform-wide.
  [STREAM_ADDRESS_WATCH]: { global: false, tenancy: true, user: false, teams: false },
  // No publisher yet. Narrow this when one exists.
  [STREAM_MESSAGES]: FULL_AUDIENCE,
  // Catalog rows are vocabulary: no user, no teams (catalog-mgmt
  // `team_ids_field=None`; the carriers live on assignments, which are not
  // streamed). System defaults travel on the bare channel.
  [STREAM_TAGS]: { global: true, tenancy: true, user: false, teams: false },
  [STREAM_LABELS]: { global: true, tenancy: true, user: false, teams: false },
  [STREAM_ENTITIES]: { global: true, tenancy: true, user: false, teams: false },
  [STREAM_GROUPS]: { global: true, tenancy: true, user: false, teams: false },
  // Audience principals: a user, a team, or the whole rung. A global
  // notification is never pushed (notification-center skips it).
  [STREAM_NOTIFICATIONS]: { global: false, tenancy: true, user: true, teams: true },
  // Membership rows carry the full carrier set (`visible_to_team_ids`).
  [STREAM_TRANSACTION_GROUPS]: FULL_AUDIENCE,
  // Case-list changes fan out to owner/shares/teams or the case's rung; users
  // publish on their own or a member team's channel. Never platform-wide.
  [STREAM_COLLABORATION]: { global: false, tenancy: true, user: true, teams: true },
  // A property of the public chain, identical for every caller.
  [STREAM_CHAIN_TIPS]: { global: true, tenancy: false, user: false, teams: false }
};
function streamAudience(stream) {
  return isKnownStream(stream) ? STREAM_AUDIENCES[stream] : FULL_AUDIENCE;
}
var TENANCY_FREE_STREAMS = KNOWN_STREAMS.filter(
  (stream) => !STREAM_AUDIENCES[stream].tenancy
);
function isTenancyFreeStream(stream) {
  return !streamAudience(stream).tenancy;
}
var KNOWN_STREAM_SET = new Set(KNOWN_STREAMS);
function isKnownStream(stream) {
  return KNOWN_STREAM_SET.has(stream);
}

// src/model/channel.ts
var ALL_CHANNELS = "*";
var DROP_SEGMENTS = /* @__PURE__ */ new Set(["", "main", "*"]);
var TEAM_CHANNEL_PREFIX = "team#";
function teamChannelPrincipal(teamId) {
  return `${TEAM_CHANNEL_PREFIX}${String(teamId).trim()}`;
}
function streamChannelId(scope, stream) {
  if (!stream.trim()) throw new Error("streamChannelId requires a non-empty stream name");
  const parts = [scope.orgId, scope.tenantId, scope.userId].map((part) => part == null ? "" : String(part).trim()).filter((part) => part !== "" && !DROP_SEGMENTS.has(part));
  if (![scope.orgId, scope.tenantId].some((v) => v != null && !DROP_SEGMENTS.has(String(v).trim()))) {
    const customer = scope.customerId == null ? "" : String(scope.customerId).trim();
    if (!DROP_SEGMENTS.has(customer)) parts.unshift(`customer#${customer}`);
  }
  parts.push(stream.trim());
  return parts.join("-");
}
function streamChannelIdsForCaller(scope, stream, teamIds = [], options = {}) {
  const { includeGlobal = true } = options;
  const audience = streamAudience(stream);
  const ids = [];
  const add = (id) => {
    if (!ids.includes(id)) ids.push(id);
  };
  if (includeGlobal && audience.global) add(streamChannelId({}, stream));
  if (!audience.tenancy) return ids;
  const present = (v) => v != null && !DROP_SEGMENTS.has(v.trim());
  const rungs = [];
  if (present(scope.customerId)) rungs.push({ customerId: scope.customerId });
  if (present(scope.orgId)) rungs.push({ customerId: scope.customerId, orgId: scope.orgId });
  rungs.push({ customerId: scope.customerId, orgId: scope.orgId, tenantId: scope.tenantId });
  for (const rung of rungs) {
    add(streamChannelId(rung, stream));
    if (audience.user && present(scope.userId)) add(streamChannelId({ ...rung, userId: scope.userId }, stream));
    if (!audience.teams) continue;
    for (const teamId of teamIds) {
      const trimmed = String(teamId ?? "").trim();
      if (!present(trimmed)) continue;
      add(streamChannelId({ ...rung, userId: teamChannelPrincipal(trimmed) }, stream));
    }
  }
  return ids;
}
var UndeclaredAudienceError = class extends Error {
  constructor(stream, carrier) {
    super(`stream "${stream}" has no ${carrier} channels; subscribers do not listen for a ${carrier} audience on it`);
    this.stream = stream;
    this.carrier = carrier;
    this.name = "UndeclaredAudienceError";
  }
  stream;
  carrier;
};
function publishChannelIdsForRow(stream, row) {
  const audience = streamAudience(stream);
  if (!audience.tenancy) return [streamChannelId({}, stream)];
  const present = (v) => v != null && !DROP_SEGMENTS.has(String(v).trim());
  const scope = {
    customerId: row.customerId,
    orgId: row.orgId,
    tenantId: row.tenantId
  };
  const owner = present(row.ownerUserId) ? row.ownerUserId : row.userId;
  const principals = [owner, ...row.visibleToUserIds ?? []].filter(present).map((v) => String(v).trim());
  const teams = (row.teamIds ?? []).filter(present).map((v) => String(v).trim());
  if (principals.length > 0 && !audience.user) throw new UndeclaredAudienceError(stream, "user");
  if (teams.length > 0 && !audience.teams) throw new UndeclaredAudienceError(stream, "team");
  const ids = [];
  const add = (id) => {
    if (!ids.includes(id)) ids.push(id);
  };
  if (principals.length === 0 && teams.length === 0) {
    const rung = streamChannelId(scope, stream);
    if (rung !== streamChannelId({}, stream) || audience.global) add(rung);
  }
  for (const principal of principals) add(streamChannelId({ ...scope, userId: principal }, stream));
  for (const team of teams) add(streamChannelId({ ...scope, userId: teamChannelPrincipal(team) }, stream));
  return ids;
}
function pick(obj, ...keys) {
  for (const key of keys) {
    const value = obj[key];
    if (value != null) return value;
  }
  return void 0;
}
var fingerprint = stableHash;
function watchHitId(record2) {
  const address = record2.address;
  if (typeof address !== "string" || address === "") return void 0;
  if (!("address_row" in record2) && !("event" in record2)) return void 0;
  const chain = typeof record2.chain === "string" ? record2.chain : "";
  return `address-watch:${chain}:${address}`;
}
var autheDedupeKey = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const record2 = payload;
  const tx = pick(record2, "canonical_tx_id", "canonicalTxId", "tx_id", "txId", "id") ?? watchHitId(record2);
  if (tx == null) return null;
  const log = pick(record2, "log_index", "logIndex");
  const id = log == null ? String(tx) : `${String(tx)}:${String(log)}`;
  let body;
  try {
    body = JSON.stringify(record2) ?? "";
  } catch {
    return id;
  }
  return `${id}#${fingerprint(body)}`;
};

// src/streams/payload.ts
function hasBodyPointer(value) {
  return !!value && typeof value.s3_key === "string" && value.s3_key !== "";
}
var STREAM_EVENT_VERBS = /* @__PURE__ */ new Set([
  "created",
  "updated",
  "deleted"
]);
function readRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function readResourceEvent(payload) {
  const record2 = readRecord(payload);
  if (!record2) return null;
  const stream = typeof record2.stream === "string" ? record2.stream : "";
  if (!stream) return null;
  const verb = typeof record2.event === "string" ? record2.event : "";
  if (!STREAM_EVENT_VERBS.has(verb)) return null;
  const header = readRecord(record2.header);
  const id = (typeof record2.id === "string" && record2.id !== "" ? record2.id : void 0) ?? (typeof header?.id === "string" && header.id !== "" ? header.id : void 0);
  if (!id) return null;
  return {
    stream,
    resourceType: typeof record2.resource_type === "string" ? record2.resource_type : void 0,
    event: verb,
    id,
    header,
    orgId: record2.org_id ?? null,
    tenantId: record2.tenant_id ?? null,
    userId: record2.user_id ?? null
  };
}
function isStreamBatch(value) {
  if (!value || typeof value !== "object") return false;
  const record2 = value;
  return record2.batch === true && Array.isArray(record2.items);
}
function unwrapStreamBatch(payload) {
  return isStreamBatch(payload) ? payload.items : [payload];
}

// src/streams/queries.ts
var SUBSCRIBE_QUERY = (
  /* GraphQL */
  `
  subscription Subscribe($name: String!) {
    subscribe(name: $name) {
      name
      data
    }
  }
`
);
var PING_QUERY = (
  /* GraphQL */
  `
  query Ping {
    ping {
      timestamp
      status
    }
  }
`
);
var PUBLISH_MUTATION = (
  /* GraphQL */
  `
  mutation PublishToChannel($name: String!, $data: AWSJSON!) {
    publishToChannel(name: $name, data: $data) {
      name
      data
    }
  }
`
);
function subscribeDescriptor(channelId) {
  return { id: channelId, query: SUBSCRIBE_QUERY, variables: { name: channelId } };
}
function record(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function selectAutheStreamPayload(raw) {
  const root = record(raw);
  const subscribe = record(record(root?.data)?.subscribe);
  const data = subscribe?.data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data ?? raw;
}
function parsePingResult(raw) {
  const root = record(raw);
  const ping = record(record(root?.data)?.ping) ?? record(root?.ping);
  if (!ping) return null;
  return {
    timestamp: String(ping.timestamp ?? ""),
    status: String(ping.status ?? "")
  };
}
function createAppSyncPing(client) {
  return async (token) => {
    const result = await client.graphql({
      query: PING_QUERY,
      authToken: token
    });
    const parsed = parsePingResult(result);
    if (!parsed || parsed.status.toLowerCase() !== "ok") {
      throw new Error(`ping: unexpected response (${parsed?.status ?? "none"})`);
    }
  };
}
function createAppSyncPublish(client) {
  return async (name, data, token) => {
    await client.graphql({
      query: PUBLISH_MUTATION,
      variables: { name, data: JSON.stringify(data ?? null) },
      authToken: token
    });
  };
}

// src/streams/resourceChanges.ts
function connectResourceChanges(client, hub, bindings) {
  const enabled = bindings.filter((binding) => hub.resources.has(binding.resource));
  const offs = enabled.flatMap(({ resource, stream, channels }) => channels.map(
    (channel) => client.subscribeChannel(channel, (message) => {
      const event = readResourceEvent(message.payload);
      if (event?.stream !== stream) return;
      hub.ingest(
        { resource, id: event.id, operation: event.event },
        message.dedupeKey ? `stream:${message.dedupeKey}` : void 0
      );
    })
  ));
  const ids = enabled.flatMap(({ channels }) => channels.map((channel) => channel.id));
  let ready = connectionStateForChannels(client.getState(), ids).status === "open";
  offs.push(client.onState((state) => {
    const next = connectionStateForChannels(state, ids).status === "open";
    if (ids.length && next && !ready) hub.revalidate();
    ready = next;
  }));
  return () => {
    for (const off of offs) off();
  };
}
function defaultSelectPayload(raw) {
  if (raw && typeof raw === "object" && "data" in raw) {
    const data = raw.data;
    if (data && typeof data === "object") {
      const values = Object.values(data);
      return values.length === 1 ? values[0] : data;
    }
    return data;
  }
  return raw;
}
function dataLessGraphQLErrors(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const { data, errors } = raw;
  if (!Array.isArray(errors) || errors.length === 0) return void 0;
  if (data && typeof data === "object") {
    const delivered = Object.values(data).some((v) => v != null);
    if (delivered) return void 0;
  } else if (data != null) {
    return void 0;
  }
  return errors;
}
function createAppSyncTransport(options) {
  const select = options.selectPayload ?? defaultSelectPayload;
  const log = options.logger ?? createLogger(STREAMING_PACKAGE).child("appsync");
  return {
    async connect(connectOptions, handlers) {
      const subscriptions = /* @__PURE__ */ new Set();
      const resolved = options.resolve ? options.resolve(connectOptions.url) : {
        client: options.client,
        ping: options.ping,
        ready: options.ready,
        release: options.release
      };
      const client = resolved.client;
      if (!client) {
        throw new Error(
          "AppSync transport requires either `client` or `resolve` to be configured"
        );
      }
      const ping = resolved.ping;
      log.debug("connect", { region: connectOptions.region, url: connectOptions.url });
      if (resolved.ready) await resolved.ready(connectOptions.token);
      handlers.onOpen?.();
      const offActivity = resolved.onActivity?.(() => handlers.onActivity?.());
      const connection = {
        subscriptionAcknowledgements: true,
        ping: ping ? () => ping(connectOptions.token) : void 0,
        keepAliveMs: offActivity ? resolved.keepAliveMs : void 0,
        subscribe(channel) {
          if (!channel.query) {
            handlers.onChannelError?.(channel.id, new Error(`AppSync channel "${channel.id}" is missing a query`));
            return () => {
            };
          }
          log.debug("subscribe", { channel: channel.id, variables: channel.variables });
          const observable = client.graphql({
            query: channel.query,
            variables: channel.variables,
            authToken: connectOptions.token
          });
          let settled = false;
          let active = true;
          let failed = false;
          let timer;
          const clearTimer = () => {
            if (timer != null) clearTimeout(timer);
            timer = void 0;
          };
          const opened = () => {
            if (!active || failed || settled) return;
            settled = true;
            clearTimer();
            handlers.onChannelOpen?.(channel.id);
          };
          const channelFailed = (error) => {
            if (!active || failed) return;
            failed = true;
            settled = true;
            clearTimer();
            handlers.onChannelError?.(channel.id, error);
          };
          const sub = observable.subscribe({
            ready: opened,
            complete: () => channelFailed(new ChannelScopedError("Subscription completed unexpectedly")),
            next: (value) => {
              if (!active || failed) return;
              log.debug("message", { channel: channel.id });
              opened();
              const errors = dataLessGraphQLErrors(value);
              if (errors) {
                log.warn("message dropped: GraphQL errors, no data", { channel: channel.id, errors });
                return;
              }
              handlers.onMessage(channel.id, select(value, channel));
            },
            error: (error) => {
              log.warn("subscription error", { channel: channel.id, error: describeError(error) });
              if (!active || failed) return;
              if (isChannelScopedError(error)) {
                channelFailed(error);
                return;
              }
              failed = true;
              settled = true;
              clearTimer();
              handlers.onError(error);
            }
          });
          if (!client.subscriptionAcknowledgements && !settled) opened();
          if (!settled) timer = setTimeout(
            () => channelFailed(new ChannelScopedError("Subscription acknowledgement timeout")),
            options.subscriptionTimeoutMs ?? 1e4
          );
          const managed = { unsubscribe: () => {
            active = false;
            clearTimer();
            sub.unsubscribe();
          } };
          subscriptions.add(managed);
          return () => {
            managed.unsubscribe();
            subscriptions.delete(managed);
          };
        },
        close() {
          offActivity?.();
          for (const sub of subscriptions) sub.unsubscribe();
          subscriptions.clear();
          resolved.release?.();
          handlers.onClose?.();
        }
      };
      return connection;
    }
  };
}
var WS_OPEN = 1;
var DEFAULT_KEEPALIVE_MS = 3e5;
var DEFAULT_LINGER_MS = 1e3;
function base64Json(value) {
  const json = JSON.stringify(value);
  if (typeof btoa === "function") {
    const bytes = new TextEncoder().encode(json);
    let latin1 = "";
    for (const byte of bytes) latin1 += String.fromCharCode(byte);
    return btoa(latin1);
  }
  return Buffer.from(json, "utf8").toString("base64");
}
function authProtocol(endpoint, token) {
  const b64url = base64Json(authHeader(endpoint, token)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return `header-${b64url}`;
}
function appSyncRealtimeUrl(endpoint) {
  const url = new URL(endpoint);
  if (url.host.includes(".appsync-api.")) {
    const host = url.host.replace(".appsync-api.", ".appsync-realtime-api.");
    return `wss://${host}${url.pathname}`;
  }
  return `wss://${url.host}${url.pathname}/realtime`;
}
function authHeader(endpoint, token) {
  return { host: new URL(endpoint).host, Authorization: token ?? "" };
}
var APPSYNC_KEEPALIVE_INTERVAL_MS = 6e4;
var RealtimeSocket = class {
  constructor(endpoint, WS, log, lingerMs, handshakeMs) {
    this.endpoint = endpoint;
    this.WS = WS;
    this.log = log;
    this.lingerMs = lingerMs;
    this.handshakeMs = handshakeMs;
  }
  endpoint;
  WS;
  log;
  lingerMs;
  handshakeMs;
  ws = null;
  acked = null;
  resolveAck = null;
  rejectAck = null;
  keepaliveMs = DEFAULT_KEEPALIVE_MS;
  kaTimer = null;
  lingerTimer = null;
  handshakeTimer = null;
  /**
   * Connection-level holds taken by {@link open} (one per live transport
   * connection). Distinct from subscriptions so the socket survives a moment
   * with zero channels, and so a transport close releases the socket even
   * though it never subscribed anything.
   */
  holds = 0;
  subs = /* @__PURE__ */ new Map();
  /** Told about every frame that proves the socket alive (`ka`, `data`). */
  activity = /* @__PURE__ */ new Set();
  onActivity(listener) {
    this.activity.add(listener);
    return () => this.activity.delete(listener);
  }
  reportActivity() {
    for (const listener of this.activity) {
      try {
        listener();
      } catch {
      }
    }
  }
  /**
   * Take a connection-level hold and resolve once AppSync has acked the
   * handshake — i.e. once this socket is genuinely usable. Lets the transport
   * report `onOpen` on a fact rather than on having been constructed.
   */
  open(token) {
    this.cancelLinger();
    this.holds += 1;
    return this.ensureSocket(token);
  }
  /**
   * Drop a connection-level hold. When nothing is left the socket closes
   * IMMEDIATELY rather than lingering: the linger exists to absorb
   * subscribe→unsubscribe churn WITHIN a live connection (StrictMode, route
   * changes), and reusing a socket across a deliberate connection close is the
   * opposite of what the caller asked for — a reconnect triggered by a wedged
   * socket would land inside the linger window and hand back the very socket it
   * was trying to replace, reconnecting forever without ever changing anything.
   */
  release() {
    if (this.holds > 0) this.holds -= 1;
    if (this.idle()) this.teardown();
  }
  add(sub) {
    this.cancelLinger();
    this.subs.set(sub.id, sub);
    this.ensureSocket(sub.token).then(() => {
      if (this.subs.has(sub.id)) this.start(sub);
    }).catch((error) => {
      if (this.subs.has(sub.id)) {
        this.subs.delete(sub.id);
        sub.observer.error?.(error);
      }
    });
  }
  remove(id) {
    const sub = this.subs.get(id);
    if (!sub) return;
    this.subs.delete(id);
    if (sub.started && this.ws && this.ws.readyState === WS_OPEN) {
      this.send({ id, type: "stop" });
      this.log.debug("stop", { id });
    }
    if (this.idle()) this.scheduleTeardown();
  }
  /** Nothing wants this socket: no subscriptions and no connection holds. */
  idle() {
    return this.subs.size === 0 && this.holds === 0;
  }
  ensureSocket(token) {
    if (this.acked) return this.acked;
    this.acked = new Promise((resolve, reject) => {
      this.resolveAck = resolve;
      this.rejectAck = reject;
    });
    if (this.handshakeMs > 0) {
      this.handshakeTimer = setTimeout(() => {
        this.handshakeTimer = null;
        this.fail(new Error(`AppSync handshake timed out after ${this.handshakeMs}ms`));
      }, this.handshakeMs);
    }
    const realtimeUrl = appSyncRealtimeUrl(this.endpoint);
    this.log.debug("socket open", { url: realtimeUrl });
    const ws = new this.WS(realtimeUrl, ["graphql-ws", authProtocol(this.endpoint, token)]);
    this.ws = ws;
    ws.onopen = () => this.send({ type: "connection_init" });
    ws.onmessage = (event) => this.onFrame(event.data);
    ws.onerror = () => this.fail(new Error("WebSocket error (handshake rejected or network unreachable)"));
    ws.onclose = (event) => {
      const reason = event?.reason ? `, reason=${event.reason}` : "";
      this.fail(new Error(`WebSocket closed (code=${event?.code ?? "?"}${reason})`));
    };
    return this.acked;
  }
  /** The channel name a subscription is bound to (the `name` GraphQL variable). */
  channelOf(sub) {
    const name = sub?.variables?.name;
    return typeof name === "string" ? name : void 0;
  }
  /** Serialized size of a data frame's payload (debug-only; best-effort). */
  frameBytes(payload) {
    try {
      return JSON.stringify(payload)?.length;
    } catch {
      return void 0;
    }
  }
  start(sub) {
    sub.started = true;
    this.log.debug("start", { id: sub.id, channel: this.channelOf(sub) });
    this.send({
      id: sub.id,
      type: "start",
      payload: {
        data: JSON.stringify({ query: sub.query, variables: sub.variables ?? {} }),
        extensions: { authorization: authHeader(this.endpoint, sub.token) }
      }
    });
  }
  onFrame(raw) {
    const frame = this.parse(raw);
    if (!frame) return;
    const type = frame.type;
    if (type === "ka") {
      this.resetKeepalive();
      this.reportActivity();
      return;
    }
    if (type === "connection_ack") {
      this.clearHandshakeTimer();
      const ms = frame.payload?.connectionTimeoutMs;
      this.keepaliveMs = typeof ms === "number" && ms > 0 ? ms : DEFAULT_KEEPALIVE_MS;
      this.resetKeepalive();
      this.log.debug("connection_ack", { keepaliveMs: this.keepaliveMs });
      this.resolveAck?.();
      return;
    }
    if (type === "connection_error" || type === "error" && frame.id == null) {
      this.fail(frame.payload ?? new Error("AppSync connection error"));
      return;
    }
    const id = typeof frame.id === "string" ? frame.id : void 0;
    const sub = id ? this.subs.get(id) : void 0;
    this.resetKeepalive();
    switch (type) {
      case "start_ack":
        this.log.debug("subscribed", { id, channel: this.channelOf(sub) });
        sub?.observer.ready?.();
        return;
      case "data":
        this.log.debug("message", {
          id,
          channel: this.channelOf(sub),
          bytes: isDebugEnabled() ? this.frameBytes(frame.payload) : void 0
        });
        this.reportActivity();
        sub?.observer.next(frame.payload);
        return;
      case "error":
        sub?.observer.error?.(new ChannelScopedError(frame.payload ?? "AppSync subscription error"));
        return;
      case "complete":
        sub?.observer.complete?.();
        return;
      default:
        return;
    }
  }
  parse(raw) {
    if (typeof raw !== "string") {
      return raw && typeof raw === "object" ? raw : null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  send(message) {
    if (this.ws && this.ws.readyState === WS_OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
  resetKeepalive() {
    if (this.kaTimer) clearTimeout(this.kaTimer);
    this.kaTimer = setTimeout(() => {
      this.fail(new Error("AppSync keep-alive timeout"));
    }, this.keepaliveMs);
  }
  /** Close the socket after `lingerMs` if still idle; reused by re-subscribes. */
  scheduleTeardown() {
    this.cancelLinger();
    if (this.lingerMs <= 0) {
      this.teardown();
      return;
    }
    this.lingerTimer = setTimeout(() => {
      this.lingerTimer = null;
      if (this.idle()) this.teardown();
    }, this.lingerMs);
  }
  cancelLinger() {
    if (this.lingerTimer) {
      clearTimeout(this.lingerTimer);
      this.lingerTimer = null;
    }
  }
  clearHandshakeTimer() {
    if (this.handshakeTimer) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
  }
  /** Surface a connection-level failure to every subscriber, then reset. */
  fail(error) {
    this.log[this.subs.size > 0 ? "warn" : "debug"]("socket failed", {
      error: describeError(error)
    });
    this.rejectAck?.(error);
    const subs = Array.from(this.subs.values());
    this.teardown();
    for (const sub of subs) sub.observer.error?.(error);
  }
  /** Close the socket and clear all state (subscriptions are dropped). */
  teardown() {
    this.cancelLinger();
    this.clearHandshakeTimer();
    this.holds = 0;
    if (this.kaTimer) {
      clearTimeout(this.kaTimer);
      this.kaTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    this.acked = null;
    this.resolveAck = null;
    this.rejectAck = null;
    this.subs.clear();
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try {
        ws.close();
      } catch {
      }
    }
  }
};
function createAppSyncWebSocketClient(config) {
  const WS = config.webSocketImpl ?? globalThis.WebSocket;
  const doFetch = config.fetchImpl ?? globalThis.fetch?.bind(globalThis);
  const log = config.logger ?? createLogger(STREAMING_PACKAGE).child("appsync-ws");
  const lingerMs = config.socketLingerMs ?? DEFAULT_LINGER_MS;
  const handshakeMs = config.handshakeTimeoutMs ?? STREAM_HANDSHAKE_TIMEOUT_MS;
  const httpTimeoutMs = config.httpTimeoutMs ?? STREAM_HTTP_TIMEOUT_MS;
  const socket = WS ? new RealtimeSocket(config.endpoint, WS, log, lingerMs, handshakeMs) : null;
  const subscriptionClient = {
    subscriptionAcknowledgements: true,
    graphql(input) {
      const observable = {
        subscribe(observer) {
          if (!socket) {
            observer.error?.(new Error("WebSocket is not available in this environment"));
            return { unsubscribe() {
            } };
          }
          const id = randomUuid();
          socket.add({
            id,
            query: input.query,
            variables: input.variables,
            token: input.authToken,
            observer,
            started: false
          });
          let active = true;
          return {
            unsubscribe() {
              if (!active) return;
              active = false;
              socket.remove(id);
            }
          };
        }
      };
      return observable;
    }
  };
  const queryClient = {
    async graphql(input) {
      if (!doFetch) throw new Error("fetch is not available in this environment");
      const headers = { "Content-Type": "application/json" };
      if (input.authToken) headers.Authorization = input.authToken;
      const controller = new AbortController();
      const timer = httpTimeoutMs > 0 ? setTimeout(() => controller.abort(), httpTimeoutMs) : null;
      try {
        const response = await doFetch(config.endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify({ query: input.query, variables: input.variables ?? {} }),
          signal: controller.signal
        });
        if (!response.ok) {
          throw new Error(`AppSync HTTP ${response.status}`);
        }
        const json = await response.json();
        if (Array.isArray(json.errors) && json.errors.length > 0) {
          throw new Error(`AppSync GraphQL error: ${JSON.stringify(json.errors)}`);
        }
        return json;
      } finally {
        if (timer) clearTimeout(timer);
      }
    }
  };
  const ready = async (token) => {
    if (!socket) throw new Error("WebSocket is not available in this environment");
    await socket.open(token);
  };
  const release = () => socket?.release();
  const onActivity = (listener) => socket ? socket.onActivity(listener) : () => {
  };
  return { subscriptionClient, queryClient, ready, release, onActivity };
}

// src/transport/appsyncClientPool.ts
function createAppSyncClientPool(options = {}) {
  const cache = /* @__PURE__ */ new Map();
  const get = (endpoint) => {
    let entry = cache.get(endpoint);
    if (!entry) {
      entry = createAppSyncWebSocketClient({ endpoint, ...options });
      cache.set(endpoint, entry);
    }
    return entry;
  };
  return {
    queryFor: (url) => get(url).queryClient,
    resolve: (url) => {
      const { subscriptionClient, queryClient, ready, release, onActivity } = get(url);
      return {
        client: subscriptionClient,
        // Kept for transports that cannot report socket activity; the
        // connection core skips it when `keepAliveMs` is declared below.
        ping: createAppSyncPing(queryClient),
        onActivity,
        keepAliveMs: APPSYNC_KEEPALIVE_INTERVAL_MS,
        // The transport reports `onOpen` only after this resolves, so a failed
        // handshake is a failed connect attempt (backoff grows, a fallback
        // chain advances) rather than an "open" connection that immediately
        // errors on its first subscription.
        ready,
        release
      };
    },
    publishFor: (url) => createAppSyncPublish(get(url).queryClient)
  };
}

// src/transport/sseTransport.ts
var ES_CLOSED = 2;
function defaultBuildUrl(base, channel, ctx) {
  const url = new URL(base, typeof location !== "undefined" ? location.href : "http://localhost");
  url.searchParams.set("channel", channel.id);
  if (ctx.region) url.searchParams.set("region", ctx.region);
  if (ctx.token) url.searchParams.set("token", ctx.token);
  return url.toString();
}
function defaultParse(data) {
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}
function resolveFactory(options) {
  if (options.eventSourceFactory) return options.eventSourceFactory;
  const ctor = globalThis.EventSource;
  if (!ctor) {
    throw new Error("SSE transport unavailable: no EventSource in this runtime");
  }
  return (url, init) => new ctor(url, init);
}
function createSseTransport(options = {}) {
  const buildUrl = options.buildUrl ?? defaultBuildUrl;
  const parse = options.parse ?? defaultParse;
  return {
    requiresChannels: true,
    connect(connectOptions, handlers) {
      const factory = resolveFactory(options);
      const sources = /* @__PURE__ */ new Set();
      let opened = false;
      let closed = false;
      let resolveReady;
      let rejectReady;
      const ready = new Promise((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
      });
      void ready.catch(() => {
      });
      return {
        ready,
        subscribe(channel) {
          if (closed) return () => {
          };
          let source;
          try {
            source = factory(buildUrl(connectOptions.url, channel, connectOptions), {
              withCredentials: options.withCredentials
            });
          } catch (error) {
            rejectReady(error);
            handlers.onError(error);
            return () => {
            };
          }
          const onData = (ev) => {
            if (closed || !sources.has(source)) return;
            handlers.onMessage(channel.id, parse(ev.data ?? ""));
          };
          source.addEventListener("message", onData);
          if (options.eventName) source.addEventListener(options.eventName, onData);
          source.onopen = () => {
            if (closed || !sources.has(source)) return;
            if (!opened) {
              opened = true;
              resolveReady();
              handlers.onOpen?.();
            }
            handlers.onActivity?.();
          };
          source.onerror = (err) => {
            if (closed || !sources.has(source)) return;
            if (source.readyState !== void 0 && source.readyState !== ES_CLOSED) return;
            const error = err ?? new Error(`SSE channel "${channel.id}" error`);
            rejectReady(error);
            handlers.onError(error);
          };
          sources.add(source);
          return () => {
            sources.delete(source);
            source.onopen = source.onerror = null;
            source.close();
          };
        },
        close() {
          if (closed) return;
          closed = true;
          rejectReady(new Error("SSE connection closed"));
          for (const source of sources) {
            source.onopen = source.onerror = null;
            source.close();
          }
          sources.clear();
          handlers.onClose?.();
        }
      };
    }
  };
}

// src/transport/pollTransport.ts
function defaultFetchChannel(ctx) {
  const url = new URL(ctx.url, typeof location !== "undefined" ? location.href : "http://localhost");
  url.searchParams.set("channel", ctx.channel.id);
  if (ctx.region) url.searchParams.set("region", ctx.region);
  const headers = {};
  if (ctx.token) headers.authorization = `Bearer ${ctx.token}`;
  return fetch(url.toString(), { headers, signal: ctx.signal }).then((res) => {
    if (!res.ok) throw new Error(`poll ${ctx.channel.id} failed: ${res.status}`);
    return res.json();
  });
}
function createPollTransport(options = {}) {
  const fetchChannel = options.fetchChannel ?? defaultFetchChannel;
  const intervalMs = options.intervalMs ?? 5e3;
  const timeoutMs = options.timeoutMs ?? intervalMs;
  const immediate = options.immediate ?? true;
  return {
    connect(connectOptions, handlers) {
      handlers.onOpen?.();
      const timers = /* @__PURE__ */ new Set();
      const controllers = /* @__PURE__ */ new Set();
      return {
        subscribe(channel) {
          let poll = 0;
          let inFlight = false;
          let current = null;
          let unsubscribed = false;
          const tick = () => {
            if (inFlight || unsubscribed) return;
            inFlight = true;
            const controller = new AbortController();
            current = controller;
            controllers.add(controller);
            const expiry = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
            const ctx = {
              ...connectOptions,
              channel,
              poll: poll++,
              signal: controller.signal
            };
            Promise.resolve().then(() => fetchChannel(ctx)).then((result) => {
              if (controller.signal.aborted) return;
              handlers.onActivity?.();
              const items = Array.isArray(result) ? result : result == null ? [] : [result];
              for (const item of items) handlers.onMessage(channel.id, item);
            }).catch((error) => {
              if (!controller.signal.aborted) handlers.onError(error);
            }).finally(() => {
              if (expiry) clearTimeout(expiry);
              inFlight = false;
              controllers.delete(controller);
              if (current === controller) current = null;
            });
          };
          const timer = setInterval(tick, intervalMs);
          timers.add(timer);
          if (immediate) tick();
          return () => {
            unsubscribed = true;
            clearInterval(timer);
            timers.delete(timer);
            current?.abort();
            if (current) controllers.delete(current);
            current = null;
          };
        },
        close() {
          for (const timer of timers) clearInterval(timer);
          timers.clear();
          for (const controller of controllers) controller.abort();
          controllers.clear();
          handlers.onClose?.();
        }
      };
    }
  };
}

// src/transport/fallbackTransport.ts
function createFallbackTransport(transports, options = {}) {
  if (transports.length === 0) {
    throw new Error("createFallbackTransport requires at least one transport");
  }
  const establishOn = options.establishOn ?? "open";
  const openTimeoutMs = options.openTimeoutMs ?? 1e4;
  const requiresChannels = transports.some((transport) => transport.requiresChannels);
  return {
    requiresChannels,
    connect(connectOptions, handlers) {
      let earlyConnection;
      let resolveReady;
      let rejectReady;
      const ready = new Promise((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
      });
      void ready.catch(() => {
      });
      const negotiated = new Promise((resolve, reject) => {
        let index = -1;
        let settled = false;
        let disposed = false;
        let activeConn = null;
        const tracked = /* @__PURE__ */ new Map();
        const unsubs = /* @__PURE__ */ new Map();
        const publicConnection = {
          ready,
          get subscriptionAcknowledgements() {
            return activeConn?.subscriptionAcknowledgements ?? true;
          },
          /**
           * Delegate to whichever transport won.
           *
           * This wrapper used to expose no `ping` at all, so composing the
           * documented `[appsync, sse, poll]` chain silently traded the ACTIVE
           * keepalive for the message-silence heartbeat — on the one transport
           * that has a real probe, and precisely in the deployments (proxies,
           * webviews) most likely to wedge a socket open. A getter rather than a
           * fixed property because the active connection is chosen after this
           * object is built, and because a transport with no probe must keep
           * reporting `undefined` rather than a ping that always succeeds.
           */
          get ping() {
            const active = activeConn;
            const probe = active?.ping;
            return probe ? () => probe.call(active) : void 0;
          },
          get keepAliveMs() {
            return activeConn?.keepAliveMs;
          },
          subscribe(channel) {
            if (disposed) return () => {
            };
            tracked.set(channel.id, channel);
            unsubs.get(channel.id)?.();
            if (activeConn) unsubs.set(channel.id, activeConn.subscribe(channel));
            return () => {
              tracked.delete(channel.id);
              const u = unsubs.get(channel.id);
              unsubs.delete(channel.id);
              u?.();
            };
          },
          close() {
            if (disposed) return;
            disposed = true;
            connectOptions.signal?.removeEventListener("abort", abort);
            cancelAttempt();
            clearUnsubs();
            try {
              activeConn?.close();
            } catch {
            }
            activeConn = null;
            if (!settled) {
              settled = true;
              const error = new Error("fallback connection cancelled");
              rejectReady(error);
              reject(error);
            }
          }
        };
        earlyConnection = publicConnection;
        let cancelAttempt = () => {
        };
        const abort = () => publicConnection.close();
        function clearUnsubs() {
          for (const u of unsubs.values()) {
            try {
              u();
            } catch {
            }
          }
          unsubs.clear();
        }
        function attempt() {
          if (disposed) return;
          index += 1;
          const i = index;
          const transport = transports[i];
          let opened = false;
          let conn = null;
          let established = false;
          let failed = false;
          let readyResolved = true;
          let timer = null;
          cancelAttempt = () => {
            if (timer != null) clearTimeout(timer);
            timer = null;
          };
          const isCurrent = () => i === index && !disposed && !failed;
          const failover = (error) => {
            if (!isCurrent()) return;
            failed = true;
            if (timer != null) {
              clearTimeout(timer);
              timer = null;
            }
            activeConn = null;
            clearUnsubs();
            try {
              conn?.close();
            } catch {
            }
            conn = null;
            if (i + 1 >= transports.length) {
              if (!settled) {
                settled = true;
                disposed = true;
                connectOptions.signal?.removeEventListener("abort", abort);
                rejectReady(error);
                reject(error instanceof Error ? error : new Error(String(error)));
              } else {
                handlers.onError(error);
              }
              return;
            }
            options.onFallback?.({ from: i, to: i + 1, error });
            attempt();
          };
          const finalizeIfReady = () => {
            if (established || !isCurrent()) return;
            if (!conn || !readyResolved) return;
            if (establishOn === "open" && !opened) return;
            established = true;
            if (timer != null) {
              clearTimeout(timer);
              timer = null;
            }
            activeConn = conn;
            if (!settled) {
              settled = true;
              resolveReady();
              resolve(publicConnection);
            }
          };
          const subHandlers = {
            onOpen: () => {
              if (!isCurrent()) return;
              opened = true;
              handlers.onOpen?.();
              finalizeIfReady();
            },
            onMessage: (channel, payload) => {
              if (!isCurrent()) return;
              handlers.onMessage(channel, payload);
            },
            onChannelOpen: (channel) => {
              if (isCurrent()) handlers.onChannelOpen?.(channel);
            },
            onChannelError: (channel, error) => {
              if (isCurrent()) handlers.onChannelError?.(channel, error);
            },
            onActivity: () => {
              if (!isCurrent()) return;
              handlers.onActivity?.();
            },
            onError: (error) => {
              if (!isCurrent()) return;
              if (established) handlers.onError(error);
              else failover(error);
            },
            onClose: (reason) => {
              if (!isCurrent()) return;
              if (established) handlers.onClose?.(reason);
              else failover(new Error(reason ?? "transport closed before establishing"));
            }
          };
          if (openTimeoutMs > 0) {
            timer = setTimeout(() => {
              if (!established && isCurrent()) {
                failover(new Error(`transport ${i} did not establish within ${openTimeoutMs}ms`));
              }
            }, openTimeoutMs);
          }
          let result;
          try {
            result = transport.connect(connectOptions, subHandlers);
          } catch (error) {
            failover(error);
            return;
          }
          Promise.resolve(result).then((c) => {
            if (!isCurrent()) {
              try {
                c.close();
              } catch {
              }
              return;
            }
            conn = c;
            activeConn = c;
            if (c.ready) {
              readyResolved = false;
              void c.ready.then(() => {
                readyResolved = true;
                finalizeIfReady();
              }, failover);
            }
            for (const channel of tracked.values()) {
              if (!isCurrent()) break;
              const off = c.subscribe(channel);
              if (isCurrent() && !c.subscriptionAcknowledgements) handlers.onChannelOpen?.(channel.id);
              if (isCurrent()) unsubs.set(channel.id, off);
              else off();
            }
            finalizeIfReady();
          }).catch((error) => failover(error));
        }
        if (connectOptions.signal?.aborted) abort();
        else {
          connectOptions.signal?.addEventListener("abort", abort, { once: true });
          attempt();
        }
      });
      if (!requiresChannels) return negotiated;
      void negotiated.catch(() => {
      });
      return earlyConnection;
    }
  };
}

// src/transport/mockTransport.ts
function createMockTransport() {
  let active = null;
  const transport = {
    connect(_options, handlers) {
      const channels = /* @__PURE__ */ new Set();
      active = { handlers, channels };
      handlers.onOpen?.();
      const connection = {
        subscribe(channel) {
          channels.add(channel.id);
          return () => channels.delete(channel.id);
        },
        close() {
          channels.clear();
          if (active?.handlers === handlers) active = null;
          handlers.onClose?.();
        }
      };
      return connection;
    }
  };
  return {
    transport,
    emit(channel, payload) {
      if (active?.channels.has(channel)) active.handlers.onMessage(channel, payload);
    },
    drop(reason) {
      const current = active;
      active = null;
      current?.handlers.onClose?.(reason ?? "mock drop");
    },
    channels() {
      return active ? [...active.channels] : [];
    }
  };
}
function computeBackoff(retries, options = {}) {
  return computeBackoff$1(retries, {
    baseMs: STREAM_BACKOFF_BASE_MS,
    maxMs: STREAM_BACKOFF_MAX_MS,
    factor: STREAM_BACKOFF_FACTOR,
    jitter: STREAM_BACKOFF_JITTER,
    ...options
  });
}
function failoverRegion(current) {
  try {
    return convertRegionToBackupRegion(current);
  } catch {
    return current;
  }
}
function safeAlias(region) {
  try {
    return convertRegionToAlias(region);
  } catch {
    return void 0;
  }
}
function aliasEndpointResolver(template, templateRegion) {
  const templateAlias = safeAlias(templateRegion);
  return (region) => {
    if (!templateAlias) return template;
    const targetAlias = safeAlias(region);
    if (!targetAlias || targetAlias === templateAlias) return template;
    try {
      const url = new URL(template);
      const labels = url.hostname.split(".");
      const at = labels.indexOf(templateAlias);
      if (at === -1) return template;
      labels[at] = targetAlias;
      url.hostname = labels.join(".");
      return url.toString();
    } catch {
      return template;
    }
  };
}

// src/connection/healthcheck.ts
var Heartbeat = class {
  constructor(intervalMs, baseTimeoutMs, onStale) {
    this.intervalMs = intervalMs;
    this.baseTimeoutMs = baseTimeoutMs;
    this.onStale = onStale;
    this.timeoutMs = baseTimeoutMs;
  }
  intervalMs;
  baseTimeoutMs;
  onStale;
  timer = null;
  last = Date.now();
  timeoutMs;
  /**
   * (Re)start the watchdog. `minTimeoutMs` widens the silence allowance for
   * this connection only — a transport whose keep-alive cadence is longer than
   * the base timeout would otherwise be torn down between two healthy beats.
   */
  start(minTimeoutMs = 0) {
    this.timeoutMs = Math.max(this.baseTimeoutMs, minTimeoutMs);
    this.last = Date.now();
    this.stop();
    this.timer = setInterval(() => {
      if (Date.now() - this.last > this.timeoutMs) this.onStale();
    }, this.intervalMs);
  }
  /** Record activity — resets the staleness clock. */
  beat() {
    this.last = Date.now();
  }
  stop() {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
};
var MAX_AUTH_RECOVERIES = 2;
var AUTH_RECOVERY_WINDOW_MS = 10 * 6e4;
var IDENTITY_FRESH_MS = 2 * 6e4;
function isAuthRejection(message) {
  return /unauthorized|forbidden|token.*expir|expir.*token|401|403/i.test(message);
}
var StreamConnection = class {
  constructor(opts) {
    this.opts = opts;
    this.log = (opts.logger ?? createLogger(STREAMING_PACKAGE)).child("connection");
    this.state = { ...initialConnectionState(opts.initialRegion), isLeader: true };
    this.heartbeat = new Heartbeat(
      opts.heartbeatIntervalMs ?? STREAM_HEARTBEAT_INTERVAL_MS,
      opts.heartbeatTimeoutMs ?? STREAM_HEARTBEAT_TIMEOUT_MS,
      () => this.onStale()
    );
  }
  opts;
  log;
  heartbeat;
  desired = /* @__PURE__ */ new Map();
  state;
  conn = null;
  reconnectTimer = null;
  pingTimer = null;
  failureStreak = 0;
  started = false;
  connecting = false;
  connectAbort = null;
  /** When each recent identity refresh started — the windowed loop guard. */
  authRecoveryTimes = [];
  recoveringIdentity = false;
  /** When a channel last opened: the server's most recent acceptance of our identity. */
  identityAcceptedAt = 0;
  /** Bumped on every (re)connect + teardown so stale async callbacks are ignored. */
  generation = 0;
  get current() {
    return this.state;
  }
  start() {
    if (this.started) return;
    this.started = true;
    void this.open();
  }
  stop() {
    if (!this.started) return;
    this.started = false;
    this.clearReconnect();
    this.teardownConnection();
    this.failureStreak = 0;
    this.authRecoveryTimes = [];
    this.identityAcceptedAt = 0;
    this.setState({ status: ConnectionStatus.CLOSED, retries: 0 });
  }
  /**
   * Manually move the live connection to `region` (the backup, or back to the
   * primary). Resets the failure streak and immediately reconnects onto the new
   * region's endpoint. No-op if already there or not started. Used for
   * non-automatic failover (`autoRegionFailover: false`) and operator tooling.
   */
  setRegion(region) {
    if (!region || region === this.state.region) return;
    this.log.info("region switch (manual)", { from: this.state.region, to: region });
    this.clearReconnect();
    this.teardownConnection();
    this.failureStreak = 0;
    this.setState({ region, status: ConnectionStatus.RECONNECTING, retries: 0 });
    if (this.started) void this.open();
  }
  addChannel(descriptor) {
    const existing = this.desired.get(descriptor.id);
    if (existing) {
      if (sameDescriptor(existing.descriptor, descriptor)) return;
      existing.descriptor = descriptor;
      if (this.conn) {
        this.log.debug("resubscribe (descriptor changed)", { channel: descriptor.id });
        this.subscribeEntry(existing);
      }
      return;
    }
    const entry = { descriptor };
    this.desired.set(descriptor.id, entry);
    if (this.conn) {
      this.log.debug("subscribe", { channel: descriptor.id });
      this.subscribeEntry(entry);
      this.logChannels("channel added");
    } else {
      this.log.debug("subscribe (deferred \u2014 not open)", { channel: descriptor.id });
      if (this.started && this.reconnectTimer == null) void this.open();
    }
  }
  removeChannel(id) {
    const entry = this.desired.get(id);
    if (!entry) return;
    entry.unsubscribe?.();
    if (entry.retryTimer) clearTimeout(entry.retryTimer);
    this.desired.delete(id);
    const channels = { ...this.state.channels };
    delete channels[id];
    this.updateChannelStates(channels);
    if (this.started && this.desired.size === 0 && this.opts.transport.requiresChannels) {
      this.clearReconnect();
      this.teardownConnection();
      this.failureStreak = 0;
      this.setState({ status: ConnectionStatus.IDLE, retries: 0, lastError: void 0 });
      return;
    }
    if (this.conn && this.state.status === ConnectionStatus.OPEN) {
      this.logChannels("channel removed");
    }
  }
  updateChannelStates(channels) {
    const failed = Object.values(channels).find((c) => c.status === "error");
    const live = this.state.status === ConnectionStatus.OPEN || this.state.status === ConnectionStatus.DEGRADED;
    this.setState({ channels, ...live ? {
      status: failed ? ConnectionStatus.DEGRADED : ConnectionStatus.OPEN,
      lastError: failed?.error
    } : {} });
  }
  subscribeEntry(entry, retry = false) {
    if (!this.conn) return;
    if (entry.retryTimer) clearTimeout(entry.retryTimer);
    entry.retryTimer = void 0;
    entry.unsubscribe?.();
    if (!retry) entry.retries = 0;
    this.updateChannelStates({ ...this.state.channels, [entry.descriptor.id]: { status: "subscribing" } });
    entry.unsubscribe = this.conn.subscribe(entry.descriptor);
    if (this.state.channels?.[entry.descriptor.id]?.status === "subscribing" && !this.conn.subscriptionAcknowledgements) {
      this.updateChannelStates({ ...this.state.channels, [entry.descriptor.id]: { status: "open" } });
    }
  }
  handleChannelError(generation, id, error) {
    const entry = this.desired.get(id);
    if (generation !== this.generation || !entry) return;
    const message = describeError(error);
    this.updateChannelStates({ ...this.state.channels, [id]: { status: "error", error: message } });
    if (isAuthRejection(message)) {
      if (Date.now() - this.identityAcceptedAt < IDENTITY_FRESH_MS) {
        this.log.warn("channel refused although the identity was just accepted", {
          channel: id,
          reason: message
        });
        return;
      }
      void this.recoverIdentity(generation, message);
      return;
    }
    if (/throttl|too.?many|limit.?exceeded|internal|timeout|temporar|unavailable|completed unexpectedly/i.test(message) && (entry.retries ?? 0) < 3 && !entry.retryTimer) {
      const delay = computeBackoff(entry.retries ?? 0, this.opts.backoff);
      entry.retries = (entry.retries ?? 0) + 1;
      entry.retryTimer = setTimeout(() => {
        entry.retryTimer = void 0;
        if (this.started && generation === this.generation && this.desired.get(id) === entry) this.subscribeEntry(entry, true);
      }, delay);
    }
  }
  /**
   * Replace a rejected identity and reconnect with the new one.
   *
   * Bounded deliberately. A refresh that keeps producing a token the server
   * still refuses (a revoked session, a clock far out of step) must settle
   * into the visible error state rather than a refresh/reconnect loop that
   * looks like flakiness and bills every attempt — so at most
   * {@link MAX_AUTH_RECOVERIES} per {@link AUTH_RECOVERY_WINDOW_MS}.
   */
  async recoverIdentity(generation, reason) {
    if (!this.opts.refreshToken || this.recoveringIdentity) return;
    const now = Date.now();
    this.authRecoveryTimes = this.authRecoveryTimes.filter((t) => now - t < AUTH_RECOVERY_WINDOW_MS);
    if (this.authRecoveryTimes.length >= MAX_AUTH_RECOVERIES) {
      this.log.warn("authorization rejected and identity refresh exhausted", {
        reason,
        attempts: this.authRecoveryTimes.length
      });
      return;
    }
    this.recoveringIdentity = true;
    this.authRecoveryTimes.push(now);
    try {
      const token = await this.opts.refreshToken();
      if (!this.started || generation !== this.generation) return;
      if (!token) {
        this.log.warn("identity refresh produced no token", { reason });
        return;
      }
      this.log.info("identity refreshed after authorization rejection", { reason });
      this.failAndReconnect(generation, "identity refreshed");
    } catch (error) {
      this.log.warn("identity refresh threw", { reason, error: describeError(error) });
    } finally {
      this.recoveringIdentity = false;
    }
  }
  /** Info snapshot of the live channel set (mirrors the `open` log) so the active
   *  total stays visible as channels are added/removed after the connection opens. */
  logChannels(message) {
    this.log.info(message, {
      channels: this.desired.size,
      ids: Array.from(this.desired.keys())
    });
  }
  setState(patch) {
    this.state = { ...this.state, ...patch };
    this.opts.onState?.(this.state);
  }
  async open() {
    if (!this.started || this.connecting || this.conn) return;
    if (this.opts.transport.requiresChannels && this.desired.size === 0) return;
    this.connecting = true;
    const generation = ++this.generation;
    const controller = new AbortController();
    this.connectAbort = controller;
    const deadline = Date.now() + (this.opts.connectTimeoutMs ?? STREAM_CONNECT_TIMEOUT_MS);
    this.setState({
      status: this.failureStreak > 0 ? ConnectionStatus.RECONNECTING : ConnectionStatus.CONNECTING
    });
    try {
      const conn = await withTimeout(
        (async () => {
          const token = await this.opts.getToken?.();
          if (controller.signal.aborted) throw new Error("connect cancelled");
          const url = this.opts.endpoint(this.state.region);
          this.log.debug("connecting", { region: this.state.region, url });
          return this.opts.transport.connect(
            { url, region: this.state.region, token, signal: controller.signal },
            {
              onOpen: () => this.log.debug("transport open"),
              onMessage: (channel, payload) => this.handleMessage(generation, channel, payload),
              // Proof of life that is not an app message. Without it the
              // silence watchdog cannot tell a healthy IDLE connection from a
              // wedged one, and tore down a perfectly good low-traffic feed
              // every `heartbeatTimeoutMs` — three of those in a row also trip
              // automatic region failover.
              onActivity: () => this.handleActivity(generation),
              onChannelOpen: (channel) => {
                if (generation === this.generation && this.desired.has(channel)) {
                  this.identityAcceptedAt = Date.now();
                  this.updateChannelStates({ ...this.state.channels, [channel]: { status: "open" } });
                }
              },
              onChannelError: (channel, error) => this.handleChannelError(generation, channel, error),
              onError: (error) => this.failAndReconnect(generation, `transport error: ${describeError(error)}`),
              onClose: (reason) => this.failAndReconnect(generation, reason ?? "transport closed")
            }
          );
        })(),
        this.opts.connectTimeoutMs ?? STREAM_CONNECT_TIMEOUT_MS,
        controller.signal,
        (late) => {
          try {
            late.close();
          } catch (error) {
            this.log.debug("late close threw", { error: describeError(error) });
          }
        }
      );
      if (!this.started || generation !== this.generation) {
        conn.close();
        return;
      }
      this.conn = conn;
      for (const entry of this.desired.values()) {
        if (generation !== this.generation) return;
        this.subscribeEntry(entry);
      }
      if (conn.ready) {
        await withTimeout(conn.ready, Math.max(0, deadline - Date.now()), controller.signal);
      }
      if (!this.started || generation !== this.generation) return;
      this.openSucceeded();
    } catch (error) {
      this.failAndReconnect(generation, `connect threw: ${describeError(error)}`);
    } finally {
      if (generation === this.generation) this.connecting = false;
    }
  }
  openSucceeded() {
    this.failureStreak = 0;
    const keepAliveMs = this.conn?.keepAliveMs;
    this.heartbeat.start(keepAliveMs ? Math.ceil(keepAliveMs * 1.5) : 0);
    this.startPing();
    const failed = Object.values(this.state.channels ?? {}).find((c) => c.status === "error");
    this.setState({ status: failed ? ConnectionStatus.DEGRADED : ConnectionStatus.OPEN, retries: 0, lastError: failed?.error });
    this.log.info("open", {
      region: this.state.region,
      url: this.opts.endpoint(this.state.region),
      channels: this.desired.size,
      ids: Array.from(this.desired.keys())
    });
  }
  /**
   * Active keepalive: when the transport exposes `ping`, poll it so an
   * idle-but-wedged socket is caught even with zero real traffic. A successful
   * ping refreshes the heartbeat; a failed/timed-out one forces a reconnect.
   */
  startPing() {
    this.stopPing();
    if (!this.conn?.ping || this.conn.keepAliveMs) return;
    const interval = this.opts.pingIntervalMs ?? STREAM_PING_INTERVAL_MS;
    const generation = this.generation;
    this.pingTimer = setInterval(() => void this.runPing(generation), interval);
  }
  stopPing() {
    if (this.pingTimer != null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
  async runPing(generation) {
    const conn = this.conn;
    if (!conn?.ping || generation !== this.generation) return;
    const timeoutMs = this.opts.pingTimeoutMs ?? STREAM_PING_TIMEOUT_MS;
    try {
      await withTimeout(conn.ping(), timeoutMs);
      if (generation !== this.generation) return;
      this.heartbeat.beat();
      this.log.debug("ping ok", { region: this.state.region });
    } catch (error) {
      this.failAndReconnect(generation, `ping failed: ${describeError(error)}`);
    }
  }
  /**
   * Transport-level liveness that isn't an app message (a successful poll, a
   * server keep-alive). Resets the silence watchdog without touching
   * `lastEventAt`, which means "when did the app last receive data".
   */
  handleActivity(generation) {
    if (generation !== this.generation) return;
    this.heartbeat.beat();
  }
  handleMessage(generation, channel, payload) {
    if (generation !== this.generation) return;
    this.heartbeat.beat();
    this.setState({ lastEventAt: Date.now() });
    this.log.debug("message", { channel, payload });
    this.opts.onMessage(channel, payload);
  }
  onStale() {
    this.log.warn("healthcheck stale \u2014 forcing reconnect", { region: this.state.region });
    this.setState({ status: ConnectionStatus.DEGRADED });
    this.failAndReconnect(this.generation, "healthcheck stale");
  }
  failAndReconnect(generation, reason) {
    if (!this.started || generation !== this.generation) return;
    if (isAuthRejection(reason)) void this.recoverIdentity(generation, reason);
    this.teardownConnection();
    this.failureStreak += 1;
    const threshold = this.opts.regionFailoverThreshold ?? STREAM_REGION_FAILOVER_THRESHOLD;
    let region = this.state.region;
    if (this.opts.autoRegionFailover !== false && this.failureStreak % threshold === 0) {
      region = failoverRegion(region);
      if (region !== this.state.region) {
        this.log.warn("region failover (auto)", { from: this.state.region, to: region });
      }
    }
    const delay = computeBackoff(this.failureStreak - 1, this.opts.backoff);
    this.log.warn("reconnecting", { reason, delayMs: delay, attempt: this.failureStreak });
    this.setState({
      status: ConnectionStatus.RECONNECTING,
      region,
      retries: this.failureStreak,
      lastError: reason
    });
    this.clearReconnect();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.open();
    }, delay);
  }
  teardownConnection() {
    this.generation += 1;
    this.connecting = false;
    const controller = this.connectAbort;
    this.connectAbort = null;
    controller?.abort();
    this.heartbeat.stop();
    this.stopPing();
    this.setState({ channels: {} });
    for (const entry of this.desired.values()) {
      entry.unsubscribe?.();
      entry.unsubscribe = void 0;
      if (entry.retryTimer) clearTimeout(entry.retryTimer);
      entry.retryTimer = void 0;
    }
    if (this.conn) {
      try {
        this.conn.close();
      } catch (error) {
        this.log.debug("close threw", { error: describeError(error) });
      }
      this.conn = null;
    }
  }
  clearReconnect() {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
};
function sameDescriptor(a, b) {
  if (a === b) return true;
  if (a.query !== b.query) return false;
  return stableKey(a.variables) === stableKey(b.variables);
}
function stableKey(value) {
  if (!value) return "";
  try {
    return stableStringify(value, /* @__PURE__ */ new Set());
  } catch {
    return randomUuid();
  }
}
function stableStringify(value, seen) {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null";
  }
  if (seen.has(value)) throw new TypeError("cyclic variables");
  seen.add(value);
  try {
    if (Array.isArray(value)) {
      return `[${value.map((item) => stableStringify(item, seen)).join(",")}]`;
    }
    const entries = Object.entries(value).filter(([, item]) => item !== void 0).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item, seen)}`).join(",")}}`;
  } finally {
    seen.delete(value);
  }
}
function withTimeout(promise, ms, signal, onLateResolve) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = () => {
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
    };
    const abort = () => {
      finish();
      reject(new Error("connect cancelled"));
    };
    const timer = setTimeout(() => {
      finish();
      reject(new Error(`timed out after ${ms}ms`));
    }, ms);
    if (signal?.aborted) abort();
    else signal?.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => {
        if (settled) {
          onLateResolve?.(value);
          return;
        }
        finish();
        resolve(value);
      },
      (error) => {
        if (settled) return;
        finish();
        reject(error);
      }
    );
  });
}
var Deduper = class extends EventDeduper {
  constructor(capacity = STREAM_DEDUPE_CAPACITY) {
    super(capacity);
  }
};
var EnrichPipeline = class {
  enrichers = [];
  log;
  constructor(logger) {
    this.log = (logger ?? createLogger(STREAMING_PACKAGE)).child("enrich");
  }
  /** Register an enricher; returns an unregister fn. */
  use(enricher) {
    this.enrichers.push(enricher);
    return () => {
      const index = this.enrichers.indexOf(enricher);
      if (index >= 0) this.enrichers.splice(index, 1);
    };
  }
  /** Apply all enrichers in order. A throwing enricher is skipped. */
  run(payload, context) {
    let current = payload;
    for (const enricher of this.enrichers) {
      try {
        current = enricher(current, context);
      } catch (error) {
        this.log.warn("enricher threw \u2014 skipped", { channel: context.channel, error: String(error) });
      }
    }
    return current;
  }
  get size() {
    return this.enrichers.length;
  }
};
function makeTabId() {
  return `${Date.now().toString(36)}-${randomHex(8)}`;
}
function hostLockManager() {
  const locks = globalThis.navigator?.locks;
  return locks && typeof locks.request === "function" && typeof locks.query === "function" ? locks : null;
}
var LeaderElection = class {
  constructor(opts) {
    this.opts = opts;
    this.heartbeatMs = opts.heartbeatMs ?? STREAM_LEADER_HEARTBEAT_MS;
    this.staleMs = opts.staleMs ?? STREAM_LEADER_STALE_MS;
    this.supported = typeof BroadcastChannel !== "undefined";
    this.log = (opts.logger ?? createLogger(STREAMING_PACKAGE)).child("leader");
    this.channelName = opts.channelName ?? STREAM_ELECTION_CHANNEL;
    this.locks = opts.locks === void 0 ? hostLockManager() : opts.locks;
  }
  opts;
  /** This tab's unique, time-sortable id. */
  id = makeTabId();
  heartbeatMs;
  staleMs;
  supported;
  log;
  peers = /* @__PURE__ */ new Map();
  onPageHide = () => this.announceBye();
  locks;
  channelName;
  lockAbort = null;
  releaseLeaderLock = null;
  releasePresenceLock = null;
  stopped = true;
  bc = null;
  timer = null;
  isLeader = false;
  get leader() {
    return this.isLeader;
  }
  /** Live tabs participating in the election, including this one. */
  get tabCount() {
    return this.supported ? this.peers.size : 1;
  }
  /** Other live tabs — i.e. followers when this tab is the leader. */
  get followerCount() {
    return Math.max(0, this.tabCount - 1);
  }
  start() {
    this.stopped = false;
    if (!this.supported) {
      this.isLeader = true;
      this.log.debug("BroadcastChannel unavailable \u2014 single-tab leader");
      this.opts.onChange(true);
      return;
    }
    this.bc = new BroadcastChannel(this.channelName);
    this.bc.onmessage = (event) => this.onMessage(event.data);
    if (typeof addEventListener !== "undefined") addEventListener("pagehide", this.onPageHide);
    this.peers.set(this.id, Date.now());
    this.beat();
    this.timer = setInterval(() => this.tick(), this.heartbeatMs);
    if (this.locks) this.requestLocks(this.locks);
    else this.reevaluate();
  }
  /**
   * Ids of the tabs that are alive right now, per the lock manager — or `null`
   * when it cannot say (no Web Locks), in which case the caller falls back to
   * judging liveness by message recency.
   *
   * Alive means "holds its presence lock", which the browser releases only when
   * the tab goes away. A throttled background tab stays alive here however late
   * its heartbeats run.
   */
  async liveTabIds() {
    if (!this.locks || !this.supported) return null;
    const prefix = `${this.channelName}.tab.`;
    try {
      const snapshot = await this.locks.query();
      const live = /* @__PURE__ */ new Set();
      for (const lock of snapshot.held ?? []) {
        if (lock.name?.startsWith(prefix)) live.add(lock.name.slice(prefix.length));
      }
      return live;
    } catch {
      return null;
    }
  }
  requestLocks(locks) {
    const abort = new AbortController();
    this.lockAbort = abort;
    void locks.request(`${this.channelName}.tab.${this.id}`, { signal: abort.signal }, () => new Promise((release) => {
      if (this.stopped) return release();
      this.releasePresenceLock = release;
    })).catch(() => {
    });
    void locks.request(`${this.channelName}.leader`, { signal: abort.signal }, () => new Promise((release) => {
      if (this.stopped) return release();
      this.releaseLeaderLock = release;
      this.setLeader(true, this.id);
    })).catch(() => {
    });
  }
  stop() {
    this.stopped = true;
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (typeof removeEventListener !== "undefined") removeEventListener("pagehide", this.onPageHide);
    this.announceBye();
    this.bc?.close();
    this.bc = null;
    this.peers.clear();
    this.lockAbort?.abort();
    this.lockAbort = null;
    this.releaseLeaderLock?.();
    this.releaseLeaderLock = null;
    this.releasePresenceLock?.();
    this.releasePresenceLock = null;
    this.isLeader = false;
  }
  beat() {
    const now = Date.now();
    this.peers.set(this.id, now);
    this.bc?.postMessage({ type: "heartbeat", id: this.id, ts: now });
  }
  announceBye() {
    this.peers.delete(this.id);
    this.bc?.postMessage({ type: "bye", id: this.id });
  }
  onMessage(message) {
    if (!message || typeof message !== "object") return;
    if (message.type === "heartbeat") {
      const known = this.peers.has(message.id);
      this.peers.set(message.id, Date.now());
      if (!known && message.id !== this.id) this.beat();
    } else if (message.type === "bye") {
      this.peers.delete(message.id);
    } else {
      return;
    }
    this.reevaluate();
  }
  tick() {
    this.beat();
    const cutoff = Date.now() - this.staleMs;
    for (const [id, last] of this.peers) {
      if (id !== this.id && last < cutoff) this.peers.delete(id);
    }
    this.reevaluate();
  }
  reevaluate() {
    if (this.locks) return;
    let lowest = this.id;
    for (const id of this.peers.keys()) {
      if (id < lowest) lowest = id;
    }
    this.setLeader(lowest === this.id, lowest);
  }
  setLeader(shouldLead, leader) {
    if (shouldLead === this.isLeader) return;
    this.isLeader = shouldLead;
    if (shouldLead) {
      this.log.info("became leader", { id: this.id, tabs: this.tabCount, followers: this.followerCount });
    } else {
      this.log.info("follower \u2014 another tab is leader", {
        id: this.id,
        leader,
        peers: this.peers.size
      });
    }
    this.opts.onChange(shouldLead);
  }
};
var CrossTabBus = class extends BroadcastBus {
  constructor(channelName = STREAM_EVENTS_CHANNEL) {
    super(channelName, (value) => !!value && typeof value === "object");
  }
};
var StreamingClient = class {
  constructor(opts) {
    this.opts = opts;
    this.log = (opts.logger ?? createLogger(STREAMING_PACKAGE)).child("client");
    this.dedupeKey = opts.dedupeKey ?? autheDedupeKey;
    this.deduper = new Deduper(opts.dedupeCapacity ?? STREAM_DEDUPE_CAPACITY);
    this.enrich = new EnrichPipeline(this.log);
    this.shareAcrossTabs = opts.shareAcrossTabs ?? true;
    this.sharingScope = encodeURIComponent(JSON.stringify([
      opts.endpoint(opts.region),
      opts.region,
      opts.sharingKey ?? ""
    ]));
    this.regionKey = `${STREAM_REGION_KEY}:${this.sharingScope}`;
    for (const enricher of opts.enrichers ?? []) this.enrich.use(enricher);
    for (const channel of opts.channels ?? []) {
      this.channels.set(channel.id, channel);
      this.staticChannels.add(channel.id);
    }
    const region = this.resolveInitialRegion();
    this.state = initialConnectionState(region);
  }
  opts;
  log;
  deduper;
  enrich;
  dedupeKey;
  channels = /* @__PURE__ */ new Map();
  /** Upfront and explicit addChannel declarations, retained until removeChannel. */
  staticChannels = /* @__PURE__ */ new Set();
  /** Active `subscribeChannel` count per channel, for demand-driven teardown. */
  channelRefs = /* @__PURE__ */ new Map();
  /** Each peer renews its complete demand; expired peers cannot retain channels. */
  foreignDemand = /* @__PURE__ */ new Map();
  connectionChannels = /* @__PURE__ */ new Set();
  handlers = /* @__PURE__ */ new Map();
  stateListeners = /* @__PURE__ */ new Set();
  shareAcrossTabs;
  sharingScope;
  regionKey;
  demandTimer = null;
  onPageHide = () => this.releaseDemand();
  election = null;
  bus = null;
  connection = null;
  state;
  /** A connection state held back by the coalescing window (see `onConnectionState`). */
  pendingState = null;
  pendingStateTimer = null;
  started = false;
  // -- lifecycle ------------------------------------------------------------
  start() {
    if (this.started) return;
    this.started = true;
    if (this.shareAcrossTabs) {
      const scope = `${STREAM_BROADCAST_CHANNEL}.v2.${this.sharingScope}`;
      this.election = new LeaderElection({
        channelName: `${scope}.election`,
        logger: this.log,
        locks: this.opts.locks,
        onChange: (isLeader) => this.onLeadershipChange(isLeader)
      });
      this.bus = new CrossTabBus(`${scope}.events`);
      this.bus.start();
      this.bus.subscribe((message) => this.onBusMessage(message));
      this.election.start();
      this.publishDemand();
      this.bus.publish({ kind: "hello" });
      this.demandTimer = setInterval(() => {
        this.publishDemand();
        void this.pruneForeignDemand();
      }, STREAM_LEADER_HEARTBEAT_MS);
      if (typeof addEventListener !== "undefined") addEventListener("pagehide", this.onPageHide);
    } else {
      this.onLeadershipChange(true);
    }
  }
  stop() {
    if (!this.started) return;
    this.started = false;
    this.releaseDemand();
    if (this.demandTimer != null) clearInterval(this.demandTimer);
    this.demandTimer = null;
    if (typeof removeEventListener !== "undefined") removeEventListener("pagehide", this.onPageHide);
    this.election?.stop();
    this.election = null;
    const connection = this.connection;
    this.connection = null;
    connection?.stop();
    this.connectionChannels.clear();
    this.bus?.stop();
    this.bus = null;
    this.foreignDemand.clear();
    this.dropPendingState();
    this.setState({ status: ConnectionStatus.CLOSED, isLeader: false });
  }
  /** Alias for `stop()` — frees all timers, sockets, and BroadcastChannels. */
  destroy() {
    this.stop();
  }
  // -- subscriptions --------------------------------------------------------
  /**
   * Attach a handler to a channel id (use `"*"` / `ALL_CHANNELS` for every
   * channel). Returns an unsubscribe fn. Channel *demand* (the GraphQL doc +
   * variables) is declared separately via `addChannel`/the `channels` option.
   */
  subscribe(channelId, handler) {
    let set = this.handlers.get(channelId);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.handlers.set(channelId, set);
    }
    set.add(handler);
    return () => {
      const current = this.handlers.get(channelId);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) this.handlers.delete(channelId);
    };
  }
  /**
   * Dynamically subscribe to a *new* channel: declares the channel on the
   * shared connection (or forwards demand to the leader) AND attaches a handler
   * in one call. Ref-counted — when the last dynamic subscriber for a channel
   * unsubscribes, the channel is removed from the connection (channels declared
   * via the `channels` option are kept). Returns an unsubscribe fn.
   */
  subscribeChannel(descriptor, handler) {
    this.channelRefs.set(descriptor.id, (this.channelRefs.get(descriptor.id) ?? 0) + 1);
    const off = this.subscribe(descriptor.id, handler);
    this.channels.set(descriptor.id, descriptor);
    this.demandChanged();
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      off();
      const remaining = (this.channelRefs.get(descriptor.id) ?? 1) - 1;
      if (remaining > 0) {
        this.channelRefs.set(descriptor.id, remaining);
        return;
      }
      this.channelRefs.delete(descriptor.id);
      if (!this.staticChannels.has(descriptor.id)) {
        this.channels.delete(descriptor.id);
        this.demandChanged();
      }
    };
  }
  /** Declare a channel to subscribe on the shared connection. */
  addChannel(descriptor) {
    this.staticChannels.add(descriptor.id);
    this.channels.set(descriptor.id, descriptor);
    this.demandChanged();
  }
  /** Release this tab's explicit declaration; live dynamic/foreign demand survives. */
  removeChannel(channelId) {
    this.staticChannels.delete(channelId);
    if (!this.channelRefs.has(channelId)) this.channels.delete(channelId);
    this.demandChanged();
  }
  /** Register an enricher applied to every event; returns an unregister fn. */
  use(enricher) {
    return this.enrich.use(enricher);
  }
  /**
   * Manually switch the active region (primary ↔ backup) and persist it so a
   * reload starts there. The leader tab reconnects its socket onto the new
   * region immediately. A follower asks the leader to, and its status follows
   * the leader's broadcast. It used to relabel only itself: the socket stayed
   * put, and the leader's next broadcast took the label back (~0.5 s), so a
   * switch from any tab but the oldest silently did nothing.
   * Pairs with `autoRegionFailover: false` for fully operator-driven failover.
   */
  setRegion(region) {
    if (!region || region === this.state.region) return;
    writeLocalJson(this.regionKey, region);
    if (this.bus && !this.isLeader()) {
      this.bus.publish({ kind: "region", region });
      return;
    }
    if (this.isLeader() && this.connection) {
      this.connection.setRegion(region);
      return;
    }
    this.setState({ region });
  }
  /** The region the shared connection is currently using. */
  getRegion() {
    return this.state.region;
  }
  /** Other live tabs sharing this client (followers when this tab leads). */
  followerCount() {
    return this.election?.followerCount ?? 0;
  }
  /** Live tabs (including this one) sharing this client. */
  tabCount() {
    return this.election?.tabCount ?? 1;
  }
  /**
   * A structured snapshot of the live streaming state — region, leadership,
   * tab/follower counts, declared channels, per-channel subscriber counts and
   * the dedupe window size. Handy from the console:
   * `window.__autheStreaming.stats()`.
   */
  snapshot() {
    const handlers = {};
    for (const [id, set] of this.handlers) handlers[id] = set.size;
    return {
      region: this.state.region,
      status: this.state.status,
      isLeader: this.isLeader(),
      tabId: this.election?.id ?? null,
      tabs: this.tabCount(),
      followers: this.followerCount(),
      channels: [...this.desiredChannels().keys()],
      handlers,
      dedupeSeen: this.deduper.size
    };
  }
  // -- state ----------------------------------------------------------------
  getState() {
    return this.state;
  }
  onState(listener) {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }
  isLeader() {
    return this.election ? this.election.leader : this.started;
  }
  // -- internals ------------------------------------------------------------
  resolveInitialRegion() {
    const persisted = readLocalJson(this.regionKey, null);
    if (persisted) {
      try {
        if (isRegionEnabled(persisted)) return persisted;
      } catch {
      }
    }
    return this.opts.region;
  }
  onLeadershipChange(isLeader) {
    if (isLeader) {
      this.foreignDemand.clear();
      this.setState({ isLeader: true });
      this.startConnection();
      this.bus?.publish({ kind: "resync" });
      return;
    }
    const connection = this.connection;
    this.connection = null;
    this.connectionChannels.clear();
    this.foreignDemand.clear();
    connection?.stop();
    this.dropPendingState();
    this.setState({
      isLeader: false,
      status: ConnectionStatus.IDLE,
      retries: 0,
      lastError: void 0
    });
    this.bus?.publish({ kind: "hello" });
    this.publishDemand();
  }
  startConnection() {
    if (this.connection) return;
    const connection = new StreamConnection({
      transport: this.opts.transport,
      endpoint: this.opts.endpoint,
      initialRegion: this.state.region,
      getToken: this.opts.getToken,
      refreshToken: this.opts.refreshToken,
      backoff: this.opts.backoff,
      heartbeatIntervalMs: this.opts.heartbeatIntervalMs,
      heartbeatTimeoutMs: this.opts.heartbeatTimeoutMs,
      pingIntervalMs: this.opts.pingIntervalMs,
      pingTimeoutMs: this.opts.pingTimeoutMs,
      connectTimeoutMs: this.opts.connectTimeoutMs,
      regionFailoverThreshold: this.opts.regionFailoverThreshold,
      autoRegionFailover: this.opts.autoRegionFailover,
      logger: this.log,
      onMessage: (channel, payload) => this.onConnectionMessage(channel, payload),
      onState: (state) => this.onConnectionState(connection, state)
    });
    this.connection = connection;
    this.syncChannels();
    connection.start();
  }
  /** Approx serialized size of a payload (bytes) for debug logs; undefined if unserializable. */
  bytesOf(payload) {
    try {
      const json = JSON.stringify(payload);
      return json == null ? void 0 : json.length;
    } catch {
      return void 0;
    }
  }
  /**
   * Leader: a fresh message off the wire.
   *
   * The backend may coalesce several rows into one message for a channel, so
   * expand first and run the per-event path once per item. Everything
   * downstream (dedupe, enrich, handlers, cross-tab broadcast) therefore sees
   * individual events and never has to know about batching.
   */
  onConnectionMessage(channel, payload) {
    for (const item of unwrapStreamBatch(payload)) {
      this.onConnectionEvent(channel, item);
    }
  }
  /** Leader: one event off the wire. Dedupe → enrich → deliver + broadcast. */
  onConnectionEvent(channel, payload) {
    const key = this.dedupeKey(payload, channel);
    this.log.debug("received", {
      channel,
      key,
      dedupe: key == null ? "unkeyed" : "checking",
      region: this.state.region,
      bytes: this.bytesOf(payload),
      followers: this.followerCount()
    });
    if (!this.deduper.accept(key)) {
      this.log.debug("dropped duplicate", { channel, key, seen: this.deduper.size, region: this.state.region });
      return;
    }
    const enriched = this.enrich.run(payload, { channel });
    const message = {
      channel,
      payload: enriched,
      dedupeKey: key,
      receivedAt: Date.now(),
      origin: "local"
    };
    this.deliver(message);
    this.bus?.publish({ kind: "event", message });
  }
  onConnectionState(connection, state) {
    if (this.connection !== connection) return;
    if (state.region !== this.state.region) {
      writeLocalJson(this.regionKey, state.region);
    }
    const next = { ...state, isLeader: this.isLeader() };
    if (headlineChanged(this.state, next)) {
      this.emitConnectionState(next);
      return;
    }
    if (next.channels === this.state.channels && next.lastEventAt === this.state.lastEventAt) {
      this.dropPendingState();
      return;
    }
    this.pendingState = { connection, state: next };
    this.pendingStateTimer ??= setTimeout(() => {
      this.pendingStateTimer = null;
      const pending = this.pendingState;
      this.pendingState = null;
      if (pending && pending.connection === this.connection) this.emitConnectionState(pending.state);
    }, STREAM_STATE_COALESCE_MS);
  }
  emitConnectionState(state) {
    this.dropPendingState();
    this.setState(state);
    this.bus?.publish({ kind: "state", state });
  }
  dropPendingState() {
    this.pendingState = null;
    if (this.pendingStateTimer != null) clearTimeout(this.pendingStateTimer);
    this.pendingStateTimer = null;
  }
  onBusMessage(message) {
    switch (message.kind) {
      case "event": {
        const accepted = this.deduper.accept(message.message.dedupeKey);
        this.log.debug("received (broadcast)", {
          channel: message.message.channel,
          key: message.message.dedupeKey,
          accepted
        });
        if (!accepted) return;
        this.deliver({ ...message.message, origin: "broadcast" });
        return;
      }
      case "state": {
        if (!this.isLeader()) this.setState({ ...message.state, isLeader: false });
        return;
      }
      case "demand": {
        if (this.isLeader() && message.owner !== this.election?.id) {
          this.foreignDemand.set(message.owner, {
            descriptors: message.descriptors,
            seenAt: Date.now()
          });
          this.syncChannels();
        }
        return;
      }
      case "leave": {
        if (this.foreignDemand.delete(message.owner)) this.syncChannels();
        return;
      }
      case "region": {
        if (this.isLeader() && typeof message.region === "string") this.setRegion(message.region);
        return;
      }
      case "hello": {
        if (this.isLeader()) this.bus?.publish({ kind: "state", state: this.pendingState?.state ?? this.state });
        return;
      }
      case "resync": {
        this.publishDemand();
        return;
      }
    }
  }
  /**
   * Forget the channels of tabs that are gone.
   *
   * Demand is renewed on a 2 s timer, and a background tab cannot keep that
   * timer: throttled, its renewals arrive once a minute, so judging liveness by
   * recency alone pruned a perfectly live hidden tab's channels ~54 s of every
   * 60 — unsubscribed off the shared socket, then re-subscribed (and
   * re-authorized) when the next renewal landed. With Web Locks the election
   * can say which tabs are really alive, and only a tab that is BOTH quiet and
   * gone loses its demand. The quiet window still applies first, as a grace for
   * a tab whose presence lock has not been granted yet.
   */
  async pruneForeignDemand() {
    if (this.foreignDemand.size === 0) return;
    const live = await this.election?.liveTabIds() ?? null;
    const cutoff = Date.now() - STREAM_LEADER_STALE_MS;
    let removed = false;
    for (const [owner, demand] of this.foreignDemand) {
      if (demand.seenAt >= cutoff) continue;
      if (live?.has(owner)) continue;
      this.foreignDemand.delete(owner);
      removed = true;
    }
    if (removed) this.syncChannels();
  }
  publishDemand() {
    if (!this.election) return;
    this.bus?.publish({
      kind: "demand",
      owner: this.election.id,
      descriptors: [...this.channels.values()]
    });
  }
  releaseDemand() {
    if (this.election) this.bus?.publish({ kind: "leave", owner: this.election.id });
  }
  demandChanged() {
    this.syncChannels();
    this.publishDemand();
  }
  desiredChannels() {
    const desired = /* @__PURE__ */ new Map();
    for (const demand of this.foreignDemand.values()) {
      for (const descriptor of demand.descriptors) desired.set(descriptor.id, descriptor);
    }
    for (const descriptor of this.channels.values()) desired.set(descriptor.id, descriptor);
    return desired;
  }
  syncChannels() {
    if (!this.connection) return;
    const desired = this.desiredChannels();
    for (const id of this.connectionChannels) {
      if (!desired.has(id)) {
        this.connection.removeChannel(id);
        this.connectionChannels.delete(id);
      }
    }
    for (const descriptor of desired.values()) {
      this.connection.addChannel(descriptor);
      this.connectionChannels.add(descriptor.id);
    }
  }
  deliver(message) {
    const direct = this.handlers.get(message.channel)?.size ?? 0;
    const wildcard = this.handlers.get(ALL_CHANNELS)?.size ?? 0;
    const handlers = direct + wildcard;
    this.log.debug("deliver", {
      channel: message.channel,
      handlers,
      origin: message.origin,
      region: this.state.region,
      followers: this.followerCount(),
      ...handlers === 0 ? { subscribed: [...this.handlers.keys()] } : {}
    });
    this.fanOut(this.handlers.get(message.channel), message);
    this.fanOut(this.handlers.get(ALL_CHANNELS), message);
  }
  fanOut(set, message) {
    if (!set) return;
    for (const handler of set) {
      try {
        handler(message);
      } catch (error) {
        this.log.warn("subscriber threw", { channel: message.channel, error: String(error) });
      }
    }
  }
  setState(patch) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.stateListeners) {
      try {
        listener(this.state);
      } catch {
      }
    }
  }
};
function headlineChanged(previous, next) {
  return previous.status !== next.status || previous.region !== next.region || previous.isLeader !== next.isLeader || previous.retries !== next.retries || previous.lastError !== next.lastError;
}

// src/client/createStreamingClient.ts
function createStreamingClient(options) {
  return new StreamingClient(options);
}
var CONTEXT_KEY = /* @__PURE__ */ Symbol.for("@authe/streaming.StreamingContext");
function resolveContext() {
  const registry = globalThis;
  const existing = registry[CONTEXT_KEY];
  if (existing) return existing;
  const created = React5.createContext(null);
  created.displayName = "StreamingContext";
  registry[CONTEXT_KEY] = created;
  return created;
}
var StreamingContext = resolveContext();
function useStreamingClient() {
  const client = React5.useContext(StreamingContext);
  if (!client) {
    throw new Error("useStreamingClient must be used within a <StreamingProvider>.");
  }
  return client;
}
function useOptionalStreamingClient() {
  return React5.useContext(StreamingContext);
}
function StreamingProvider({
  client,
  autoStart = true,
  stopOnUnmount = false,
  children
}) {
  React5.useEffect(() => {
    if (autoStart) client.start();
    return () => {
      if (stopOnUnmount) client.stop();
    };
  }, [client, autoStart, stopOnUnmount]);
  return /* @__PURE__ */ jsx(StreamingContext.Provider, { value: client, children });
}
function parseChainTip(payload) {
  if (!payload || typeof payload !== "object") return null;
  const record2 = payload;
  const chainId = toCanonicalChainId(String(record2.chain_id ?? ""));
  const headBlock = Number(record2.head_block);
  if (!chainId || !Number.isFinite(headBlock) || headBlock <= 0) return null;
  const tip = { chainId, headBlock };
  const confirmedBlock = Number(record2.confirmed_block);
  if (Number.isFinite(confirmedBlock)) tip.confirmedBlock = confirmedBlock;
  if (typeof record2.updated_at === "string") tip.updatedAt = record2.updated_at;
  return tip;
}
function useChainTips(options = {}) {
  const { enabled = true } = options;
  const client = useOptionalStreamingClient();
  const [tips, setTips] = React5.useState({});
  React5.useEffect(() => {
    if (!client || !enabled) return;
    const descriptor = subscribeDescriptor(streamChannelId({}, STREAM_CHAIN_TIPS));
    const handler = (message) => {
      const tip = parseChainTip(message.payload);
      if (!tip) return;
      setTips((current) => {
        const previous = current[tip.chainId];
        if (previous && previous.headBlock >= tip.headBlock) return current;
        return { ...current, [tip.chainId]: tip };
      });
    };
    return client.subscribeChannel(descriptor, handler);
  }, [client, enabled]);
  return tips;
}
function headForChain(tips, chainId) {
  const key = toCanonicalChainId(chainId);
  return key ? tips[key]?.headBlock ?? null : null;
}
function useConnectionStatus(channelIds) {
  const client = useStreamingClient();
  const state = React5.useSyncExternalStore(
    React5.useCallback((onChange) => client.onState(onChange), [client]),
    () => client.getState(),
    () => client.getState()
  );
  return connectionStateForChannels(state, channelIds);
}
function useStream(channel, options = {}) {
  const { onMessage, enabled = true } = options;
  const client = useStreamingClient();
  const [last, setLast] = React5.useState(null);
  const onMessageRef = React5.useRef(onMessage);
  React5.useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  const descriptor = typeof channel === "string" ? null : channel;
  const channelId = typeof channel === "string" ? channel : channel.id;
  const descriptorKey = descriptor ? JSON.stringify(descriptor) : channelId;
  const [seen, setSeen] = React5.useState(descriptorKey);
  if (seen !== descriptorKey) {
    setSeen(descriptorKey);
    setLast(null);
  }
  React5.useEffect(() => {
    if (!enabled) return;
    const handler = (message) => {
      setLast(message);
      onMessageRef.current?.(message);
    };
    return descriptor ? client.subscribeChannel(descriptor, handler) : client.subscribe(channelId, handler);
  }, [client, channelId, descriptorKey, enabled]);
  return last;
}
var useAppSyncSubscription = useStream;

export { ALL_CHANNELS, APPSYNC_KEEPALIVE_INTERVAL_MS, ChannelScopedError, ConnectionStatus, CrossTabBus, Deduper, EnrichPipeline, Heartbeat, KNOWN_STREAMS, LeaderElection, PING_QUERY, PUBLISH_MUTATION, STREAMING_PACKAGE, STREAM_ADDRESS_WATCH, STREAM_AUDIENCES, STREAM_BACKOFF_BASE_MS, STREAM_BACKOFF_FACTOR, STREAM_BACKOFF_JITTER, STREAM_BACKOFF_MAX_MS, STREAM_BROADCAST_CHANNEL, STREAM_CHAIN_TIPS, STREAM_COLLABORATION, STREAM_CONNECT_TIMEOUT_MS, STREAM_DEDUPE_CAPACITY, STREAM_ELECTION_CHANNEL, STREAM_ENTITIES, STREAM_EVENTS_CHANNEL, STREAM_GROUPS, STREAM_HANDSHAKE_TIMEOUT_MS, STREAM_HEARTBEAT_INTERVAL_MS, STREAM_HEARTBEAT_TIMEOUT_MS, STREAM_HTTP_TIMEOUT_MS, STREAM_LABELS, STREAM_LEADER_HEARTBEAT_MS, STREAM_LEADER_STALE_MS, STREAM_MESSAGES, STREAM_NOTIFICATIONS, STREAM_PING_INTERVAL_MS, STREAM_PING_TIMEOUT_MS, STREAM_REGION_FAILOVER_THRESHOLD, STREAM_REGION_KEY, STREAM_STATE_COALESCE_MS, STREAM_STREAMED_TRANSACTIONS, STREAM_TAGS, STREAM_TRANSACTION_GROUPS, SUBSCRIBE_QUERY, StreamConnection, StreamingClient, StreamingContext, StreamingProvider, TEAM_CHANNEL_PREFIX, TENANCY_FREE_STREAMS, UndeclaredAudienceError, aliasEndpointResolver, appSyncRealtimeUrl, autheDedupeKey, computeBackoff, connectResourceChanges, connectionStateForChannels, createAppSyncClientPool, createAppSyncPing, createAppSyncPublish, createAppSyncTransport, createAppSyncWebSocketClient, createFallbackTransport, createMockTransport, createPollTransport, createSseTransport, createStreamingClient, dataLessGraphQLErrors, describeError, failoverRegion, hasBodyPointer, headForChain, initialConnectionState, isChannelScopedError, isKnownStream, isStreamBatch, isTenancyFreeStream, parsePingResult, publishChannelIdsForRow, readResourceEvent, selectAutheStreamPayload, streamAudience, streamChannelId, streamChannelIdsForCaller, subscribeDescriptor, teamChannelPrincipal, unwrapStreamBatch, useAppSyncSubscription, useChainTips, useConnectionStatus, useOptionalStreamingClient, useStream, useStreamingClient };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map